import { useRef } from "react";
import { toast } from "sonner";
import { padCh } from "@/components/daw/dawHelpers";

/**
 * Channel load / drop / clear handlers — extracted verbatim from
 * ELPMStudioDAW. Everything that puts audio (or a bounced MIDI file) onto
 * a mixer channel and keeps the transport React state in sync.
 */
export default function useChannelLoadHandlers({
  engine,
  setBusy,
  setMaxDur,
  setLoadedCount,
  selectedChannel,
  setSelectedChannel,
}) {
  const handleUpload = async (idx, file) => {
    try {
      const buf = await engine.loadFileToChannel(idx, file);
      setMaxDur(engine.maxDuration);
      setLoadedCount((c) => c + 1);
      toast.success(`Loaded into CH ${padCh(idx)}`, { description: `${file.name} · ${buf.duration.toFixed(2)}s` });
    } catch (e) {
      toast.error("Could not decode this file", { description: e.message });
    }
  };

  /** Parse + bounce a MIDI file into an AudioBuffer and load it into a channel. */
  const loadMidiToChannel = async (idx, file) => {
    const { parseMidiFile, renderMidiToBuffer, summariseMidi } = await import("@/lib/midiRender");
    const midi = await parseMidiFile(file);
    if (!midi) {
      toast.error("Could not parse MIDI file", { description: `${file.name} is not a valid Standard MIDI File.` });
      return;
    }
    setBusy("midi");
    try {
      const info = await renderMidiToBuffer(midi, engine.ctx);
      engine.loadBufferToChannel(idx, info.buffer, file.name.replace(/\.midi?$/i, ""));
      setMaxDur(engine.maxDuration);
      setLoadedCount(engine.channels.filter((c) => c.buffer).length);
      toast.success(`MIDI rendered into CH ${padCh(idx)}`, {
        description: `${file.name} · ${summariseMidi(midi, info)}`,
        duration: 8000,
      });
    } catch (e) {
      toast.error("MIDI render failed", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  /** Drop a single file onto a specific channel (used by per-lane drag-drop). */
  const handleDropOnChannel = async (idx, file) => {
    const name = file?.name || "";
    const isMidi = /\.(mid|midi)$/i.test(name);
    const isAudio = file?.type?.startsWith("audio/") || /\.(wav|mp3|ogg|m4a|aac|flac|webm)$/i.test(name);
    if (!isAudio && !isMidi) {
      toast.error("Not an audio or MIDI file", { description: `${name || "(unknown)"} — drop WAV / MP3 / OGG / FLAC / M4A / MID.` });
      return;
    }
    if (isMidi) {
      await loadMidiToChannel(idx, file);
    } else {
      await handleUpload(idx, file);
    }
    setSelectedChannel(idx);
  };

  /** Drop one OR many files onto the timeline at large — fans them across
   *  consecutive empty channels starting from `firstIdx` (or first empty). */
  const handleDropOnTimeline = async (firstIdx, files) => {
    const list = Array.from(files || []).filter((f) =>
      f.type.startsWith("audio/") || /\.(wav|mp3|ogg|m4a|aac|flac|webm|mid|midi)$/i.test(f.name)
    );
    if (list.length === 0) {
      toast.error("No audio or MIDI files in drop", { description: "Drop WAV / MP3 / OGG / FLAC / M4A / MID files only." });
      return;
    }
    let target = firstIdx ?? engine.channels.findIndex((c) => !c.buffer);
    if (target < 0) target = selectedChannel;
    for (const file of list) {
      while (target < engine.channels.length && engine.channels[target].buffer) target++;
      if (target >= engine.channels.length) {
        toast.warning("Ran out of channels", { description: `Skipped ${list.length - list.indexOf(file)} file(s). Clear some channels to make room.` });
        break;
      }
      if (/\.(mid|midi)$/i.test(file.name)) {
        await loadMidiToChannel(target, file);
      } else {
        await handleUpload(target, file);
      }
      target++;
    }
    if (list.length > 1) {
      toast.success(`Loaded ${Math.min(list.length, engine.channels.length - (firstIdx ?? 0))} files into the timeline`);
    }
  };

  const handleClear = (idx) => {
    const c = engine.channels[idx];
    c.stop();
    c.buffer = null;
    c.name = null;
    c.waveform = null;
    engine.maxDuration = Math.max(0, ...engine.channels.map((ch) => ch.duration || 0));
    setMaxDur(engine.maxDuration);
    setLoadedCount((n) => Math.max(0, n - 1));
    toast.message(`CH ${padCh(idx)} cleared`);
  };

  const headerUploadRef = useRef(null);
  const handleHeaderUpload = async (file) => {
    let target = engine.channels.findIndex((c) => !c.buffer);
    if (target === -1) target = selectedChannel;
    await handleUpload(target, file);
    setSelectedChannel(target);
  };
  const handleHeaderUploadClick = () => headerUploadRef.current?.click();
  const handleHeaderFilePicked = (e) => {
    const f = e.target.files?.[0]; if (f) handleHeaderUpload(f); e.target.value = "";
  };

  return {
    handleUpload,
    handleDropOnChannel,
    handleDropOnTimeline,
    handleClear,
    headerUploadRef,
    handleHeaderUploadClick,
    handleHeaderFilePicked,
  };
}
